// Sources de données — one source's page (/connectors?source=<key>): its
// health from the API (7-day reads, found share, failures, last success,
// failing since), then four views (?sourceTab=): « Réglages » (the five steps
// of a guided API source, or the facts of a catalogue / converted one),
// « Historique » (the last live reads), « Vérification » (the comparison with
// the mechanism it will replace, while it lasts) and « Mode expert » (the
// connector studio editor; a legacy source shows « Convertir » there).
// « Remplacer maintenant » ends the comparison; deleting goes through a
// confirmation.
// API: GET /api/integrations/connectors/{key}, /{key}/history?limit=50,
// /{key}/shadow-runs?limit=50; PUT /{key} (go live = spec without shadow_of);
// DELETE /{key}. Stats come from the listing (GET /connectors → stats).

import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import {
    Activity, CircleCheck, Clock, Code, Database, History, ListChecks, PackageSearch, RefreshCw, Scale, Trash2,
} from 'lucide-react';
import {
    Badge, Banner, Card, ConfirmDialog, DescriptionList, EmptyState, Kpi, OverflowMenu, Tabs,
} from '../design/ui';
import ConnectorStudio from './ConnectorStudio';
import { API, ApiWizard, authHeaders } from './DataSourcesCreate';
import { categoryTitle, STATE_TEXT, STATE_TONE } from './dataSources/SourcesByCategory';
import {
    formatCount, formatMs, formatPercent, formatRelative, formatShortDay, formatStamp, reasonText, verificationEnd,
} from './dataToolsModel';

const SOURCE_TABS = ['steps', 'history', 'verification', 'expert'];

export const stateOf = (doc) => {
    if (doc.legacy) return 'legacy';
    if (doc.enabled === false) return 'off';
    if (doc.health?.status === 'failing') return 'failing';
    if (doc.shadow_of) return 'verification';
    return 'ok';
};

const systemLabel = (doc, t) => {
    const system = doc.presentation?.system || doc.source?.ref || doc.source?.kind;
    if (doc.brand_id) return t('dataSources.system.brand', 'API de suivi de marque');
    if (system === 'effitrace' || doc.connector_type === 'effitrace') return 'EffiTrace';
    if (doc.connector_type === 'ship24') return 'Ship24';
    return {
        http_json: t('dataSources.system.api', 'API'), http_form: t('dataSources.system.web', 'Site web'), soap: 'SOAP', browser_recipe: t('dataSources.system.portal', 'Portail (parcours)'),
    }[doc.transport] || doc.transport || '—';
};

const MODE_TEXT = { enrich_on_ingest: 'enrichir à l’ingestion', ai_tool: 'outil de l’agent', scheduled_pull: 'rafraîchissement planifié', outbound: 'sortant' };
const AUTH_TEXT = { header: 'Clé dans un en-tête', basic: 'Identifiant et mot de passe', bearer: 'Jeton', oauth2: 'OAuth2 client credentials', none: 'Sans authentification' };
const AUTH_EXTRA = { portal: 'Compte du portail', query: 'Clé dans l’adresse', form_login: 'Formulaire de connexion' };
const authLabel = (type, t) => (AUTH_TEXT[type]
    ? t(`dataSources.auth.${type}`, AUTH_TEXT[type])
    : t(`dataToolsUi.auth.${type}`, AUTH_EXTRA[type] || type));

const identifiersText = (identifiers) => Object.entries(identifiers || {}).map(([k, v]) => `${k}=${v}`).join(' ');

const SourcePage = ({ t, keyName, connector, tab: tabParam, onTab, onDeleted, onChanged, onConverted, canEdit }) => {
    const [doc, setDoc] = useState(null);
    const [history, setHistory] = useState(undefined);
    const [shadow, setShadow] = useState(undefined);
    const [error, setError] = useState(null);
    const [loadError, setLoadError] = useState(null);
    const [confirm, setConfirm] = useState(null); // 'delete' | 'golive'
    const [busy, setBusy] = useState(false);

    const load = useCallback(async () => {
        try {
            const res = await axios.get(`${API}/${encodeURIComponent(keyName)}`, authHeaders());
            setDoc(res.data);
            setLoadError(null);
        } catch (e) {
            setLoadError(t('dataSources.loadError', 'Impossible de charger les sources de données.'));
        }
    }, [keyName, t]);
    useEffect(() => { load(); }, [load]);

    const tab = SOURCE_TABS.includes(tabParam) && (tabParam !== 'verification' || doc?.shadow_of) ? tabParam : 'steps';

    const loadHistory = useCallback(() => {
        setHistory(undefined);
        axios.get(`${API}/${encodeURIComponent(keyName)}/history?limit=50`, authHeaders()).then((r) => setHistory(r.data)).catch(() => setHistory(null));
    }, [keyName]);

    useEffect(() => {
        if (tab === 'history') loadHistory();
        if (tab === 'verification') {
            setShadow(undefined);
            axios.get(`${API}/${encodeURIComponent(keyName)}/shadow-runs?limit=50`, authHeaders()).then((r) => setShadow(r.data)).catch(() => setShadow(null));
        }
    }, [tab, keyName, loadHistory]);

    const remove = async () => {
        setBusy(true);
        try {
            await axios.delete(`${API}/${encodeURIComponent(keyName)}`, authHeaders());
            setConfirm(null);
            onDeleted();
        } catch (e) {
            setConfirm(null);
            setError(t('dataSources.deleteError', 'Suppression impossible.'));
        } finally {
            setBusy(false);
        }
    };
    const goLive = async () => {
        setBusy(true);
        try {
            const spec = { ...doc.spec };
            delete spec.shadow_of;
            await axios.put(`${API}/${encodeURIComponent(keyName)}`, { spec, base_url: doc.base_url, auth: doc.auth, enabled: true }, authHeaders());
            setConfirm(null);
            await load();
            onChanged?.(t('dataToolsUi.source.liveDone', '« {{title}} » enrichit maintenant les tickets.', { title: doc.title || doc.key }));
        } catch (e) {
            setConfirm(null);
            setError(t('dataSources.saveError', 'Enregistrement impossible.'));
        } finally {
            setBusy(false);
        }
    };

    if (!doc) {
        return loadError ? (
            <Banner tone="danger" actions={<button type="button" className="u-btn u-btn--secondary u-btn--sm" onClick={load}>{t('dataToolsUi.retry', 'Réessayer')}</button>}>
                {loadError}
            </Banner>
        ) : (
            <div className="ds-skeleton" aria-busy="true" aria-label={t('dataSources.loading', 'Chargement…')}>
                {[0, 1, 2].map((i) => <span key={i} className="u-skeleton" />)}
            </div>
        );
    }

    const state = stateOf(doc);
    const guided = doc.spec?.transport === 'http_json' && (doc.spec?.steps || []).length === 1 && !doc.brand_id && doc.spec?.presentation?.path !== 'catalog';
    const initial = guided ? { key: doc.key, base_url: doc.base_url || '', auth: doc.auth || { type: 'none' }, enabled: doc.enabled !== false, spec: doc.spec, sample: null, saved: true } : null;
    const stats = connector?.stats;
    const health = doc.health || connector?.health;
    const verification = doc.spec?.verification || connector?.verification;
    const endsAt = doc.shadow_of ? verificationEnd(verification) : null;

    const renderKpis = () => {
        if (!stats && !health) return null;
        const reads = stats?.reads_7d;
        return (
            <div className="u-kpi-row ds-source-kpis">
                {stats && (
                    <Kpi
                        icon={PackageSearch}
                        tone={reads ? 'success' : 'neutral'}
                        label={t('dataToolsUi.source.kpiFound', 'Commandes trouvées')}
                        value={reads ? formatPercent(stats.found_7d / reads) : '—'}
                        context={t('dataToolsUi.source.kpiFoundContext', '{{found}} sur {{reads}} lectures · 7 jours', { found: formatCount(stats.found_7d), reads: formatCount(reads) })}
                        meter={reads ? { value: stats.found_7d, max: reads, tone: 'success', label: t('dataToolsUi.source.kpiFound', 'Commandes trouvées') } : undefined}
                        onClick={() => onTab('history')}
                        active={tab === 'history'}
                    />
                )}
                {stats && (
                    <Kpi
                        icon={Activity}
                        tone={stats.failed_7d ? 'danger' : 'neutral'}
                        label={t('dataToolsUi.source.kpiFailed', 'Échecs de lecture')}
                        value={formatCount(stats.failed_7d)}
                        context={t('dataToolsUi.source.kpiFailedContext', 'sur 7 jours, hors « commande introuvable »')}
                        contextTone={stats.failed_7d ? 'danger' : undefined}
                    />
                )}
                {stats && (
                    <Kpi
                        icon={Clock}
                        label={t('dataToolsUi.source.kpiLast', 'Dernier succès')}
                        value={stats.last_success_at ? formatRelative(stats.last_success_at) : '—'}
                        title={stats.last_success_at ? formatStamp(stats.last_success_at) : undefined}
                        context={stats.last_success_at ? t('dataToolsUi.source.kpiLastContext', 'dernière commande trouvée') : t('dataToolsUi.source.kpiLastNone', 'aucune commande trouvée encore')}
                    />
                )}
                {health && (
                    <Kpi
                        icon={health.status === 'failing' ? Activity : CircleCheck}
                        tone={health.status === 'failing' ? 'danger' : health.status === 'ok' ? 'success' : 'neutral'}
                        label={t('dataToolsUi.source.kpiHealth', 'Santé')}
                        value={health.status === 'failing'
                            ? t('dataToolsUi.source.healthFailing', 'En échec')
                            : health.status === 'ok' ? t('dataToolsUi.source.healthOk', 'Bonne') : t('connectorStudio.healthUnknown', 'inconnu')}
                        context={health.status === 'failing'
                            ? t('dataToolsUi.source.healthFailingContext', '{{count}} échecs de suite depuis le {{date}}', { count: health.consecutive || 0, date: formatStamp(health.first_failure_at) })
                            : t('dataToolsUi.source.healthOkContext', 'aucun échec en cours')}
                        contextTone={health.status === 'failing' ? 'danger' : undefined}
                    />
                )}
            </div>
        );
    };

    const renderSettings = () => {
        if (guided) {
            return <ApiWizard t={t} initial={initial} onBack={null} onDone={() => {}} onSaved={() => { load(); onChanged?.(t('dataToolsUi.source.saved', 'Source enregistrée.')); }} />;
        }
        return (
            <Card icon={ListChecks} title={t('dataToolsUi.source.factsTitle', 'Réglages de la source')}
                description={doc.legacy
                    ? t('settingsPolishUi.dataSources.legacySettings', 'Source héritée : ses réglages s’éditent une fois convertie, depuis le mode expert.')
                    : t('dataSources.notGuided', 'Cette source vient du catalogue ou d’une conversion : ses réglages s’éditent en mode expert. Les identifiants de connexion se modifient en réinstallant l’entrée du catalogue.')}
                actions={<button type="button" className="u-btn u-btn--secondary u-btn--sm" onClick={() => onTab('expert')}><Code aria-hidden="true" /> {t('dataSources.tabs.expert', 'Mode expert')}</button>}>
                <DescriptionList items={[
                    { label: t('dataSources.system.label', 'Système'), value: systemLabel(doc, t) },
                    { label: t('dataSources.step1.category', 'Type de système'), value: doc.category ? categoryTitle(doc.category, t) : null },
                    { label: t('dataSources.col.state', 'État'), value: t(`dataSources.srcState.${state}`, STATE_TEXT[state] || t('connectorStudio.legacy', 'hérité')) },
                    { label: t('dataSources.step2.baseUrl', 'Adresse de l’API'), value: doc.base_url ? <span className="ds-mono">{doc.base_url}</span> : null },
                    { label: t('dataSources.usageLabel', 'Utilisation'), value: (doc.modes || []).map((m) => t(`connectorStudio.mode_${m}`, MODE_TEXT[m] || m)).join(', ') || '—' },
                    { label: t('dataToolsUi.source.inputs', 'Recherche par'), value: (doc.inputs || []).map((i) => t(`ticketWorkflow.identifier.${i}`, i)).join(', ') || null },
                    { label: t('dataToolsUi.source.access', 'Accès'), value: doc.auth?.type ? authLabel(doc.auth.type, t) : null },
                    { label: t('dataToolsUi.source.updated', 'Modifiée'), value: doc.updated_at ? formatStamp(doc.updated_at) : null },
                ]} />
            </Card>
        );
    };

    const renderHistory = () => (
        <Card icon={History} title={t('dataToolsUi.source.historyTitle', 'Dernières lectures')}
            description={history?.stats
                ? t('dataSources.history.stats', '{{found}} commandes trouvées sur {{total}} lectures en 7 jours, {{failed}} échecs.', { found: history.stats.found_7d, total: history.stats.reads_7d, failed: history.stats.failed_7d })
                : t('dataToolsUi.source.historyDesc', 'Les 50 dernières lectures faites pour de vrais tickets.')}
            actions={(
                <button type="button" className="u-btn u-btn--ghost u-btn--icon u-btn--sm" onClick={loadHistory}
                    aria-label={t('dataToolsUi.refresh', 'Actualiser')} title={t('dataToolsUi.refresh', 'Actualiser')}>
                    <RefreshCw aria-hidden="true" />
                </button>
            )}
            flush>
            {history === undefined ? (
                <div className="ds-skeleton ds-skeleton--pad" aria-busy="true">{[0, 1, 2, 3].map((i) => <span key={i} className="u-skeleton" />)}</div>
            ) : history === null ? (
                <div className="ds-pad"><Banner tone="danger">{t('dataToolsUi.source.historyError', 'L’historique n’a pas pu être chargé.')}</Banner></div>
            ) : !(history.runs || []).length ? (
                <EmptyState icon={History} title={t('dataSources.history.empty', 'Aucune lecture encore.')}>
                    {t('dataToolsUi.source.historyEmptyHint', 'Les lectures apparaissent dès qu’un ticket interroge cette source.')}
                </EmptyState>
            ) : (
                <div className="u-table-scroll">
                    <table className="u-table ds-table">
                        <thead>
                            <tr>
                                <th style={{ width: '18%' }}>{t('dataSources.history.when', 'Quand')}</th>
                                <th style={{ width: '26%' }}>{t('dataSources.history.identifiers', 'Identifiants')}</th>
                                <th style={{ width: '44%' }}>{t('dataSources.history.result', 'Résultat')}</th>
                                <th style={{ width: '12%' }} className="ds-num">{t('dataSources.history.duration', 'Durée')}</th>
                            </tr>
                        </thead>
                        <tbody>
                            {history.runs.map((run) => (
                                <tr key={run._id}>
                                    <td className="u-num" title={formatStamp(run.created_at)}>{formatStamp(run.created_at)}</td>
                                    <td className="ds-mono" title={identifiersText(run.identifiers)}>{identifiersText(run.identifiers) || '—'}</td>
                                    <td title={run.explanation}>
                                        {run.found
                                            ? <Badge tone="success" dot>{t('dataSources.history.found', 'trouvé')}</Badge>
                                            : (
                                                <span className="ds-result">
                                                    <Badge tone={run.reason === 'not_found' ? 'warning' : 'danger'} dot>
                                                        {run.reason === 'not_found' ? t('dataToolsUi.source.notFound', 'introuvable') : t('dataToolsUi.source.failed', 'échec')}
                                                    </Badge>
                                                    <span className="ds-result__text">{run.explanation || reasonText(run.reason, t)}</span>
                                                </span>
                                            )}
                                    </td>
                                    <td className="ds-num u-num">{run.duration_ms ? formatMs(run.duration_ms) : '—'}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
        </Card>
    );

    const renderVerification = () => {
        const summary = shadow?.summary;
        return (
            <div className="ds-stack">
                <Banner tone="info" title={t('dataToolsUi.source.verifyTitle', 'Comparée à « {{legacy}} »', { legacy: doc.shadow_of })}
                    actions={canEdit ? <button type="button" className="u-btn u-btn--primary u-btn--sm" onClick={() => setConfirm('golive')}>{t('dataSources.goLive', 'Remplacer maintenant')}</button> : null}>
                    {endsAt
                        ? t('dataToolsUi.source.verifyUntil', 'À chaque enrichissement, cette source lit aussi la commande ; son résultat est comparé, pas utilisé, jusqu’au {{date}}', { date: formatShortDay(endsAt.toISOString()) })
                        : t('dataToolsUi.source.verifyOpen', 'À chaque enrichissement, cette source lit aussi la commande ; son résultat est comparé, pas utilisé.')}
                </Banner>
                <Card icon={Scale} title={t('dataToolsUi.source.compareTitle', 'Comparaisons')}
                    description={summary
                        ? t('dataSources.verification.summary', 'Sur {{total}} comparaisons : {{agree}} résultats identiques, {{both}} trouvés des deux côtés, {{specOnly}} trouvés seulement par cette source, {{legacyOnly}} seulement par le mécanisme actuel, {{failures}} échecs.', {
                            total: summary.total, agree: summary.agree, both: summary.both_found, specOnly: summary.spec_only, legacyOnly: summary.legacy_only, failures: summary.failures,
                        })
                        : null}
                    flush>
                    {shadow === undefined ? (
                        <div className="ds-skeleton ds-skeleton--pad" aria-busy="true">{[0, 1, 2].map((i) => <span key={i} className="u-skeleton" />)}</div>
                    ) : shadow === null ? (
                        <div className="ds-pad"><Banner tone="danger">{t('dataToolsUi.source.compareError', 'Les comparaisons n’ont pas pu être chargées.')}</Banner></div>
                    ) : !(shadow.runs || []).length ? (
                        <EmptyState icon={Scale} title={t('dataToolsUi.source.compareEmpty', 'Aucune comparaison encore')}>
                            {t('dataToolsUi.source.compareEmptyHint', 'Elles apparaissent à l’arrivée des prochains tickets.')}
                        </EmptyState>
                    ) : (
                        <>
                            {summary.total > 0 && (
                                <div className="ds-agreement">
                                    <span className="u-num ds-agreement__value">{formatPercent(summary.agree / summary.total)}</span>
                                    <span className="ds-agreement__label">{t('dataToolsUi.source.agreeShare', 'de résultats identiques sur les {{count}} dernières comparaisons', { count: summary.total })}</span>
                                </div>
                            )}
                            <div className="u-table-scroll">
                                <table className="u-table ds-table">
                                    <thead>
                                        <tr>
                                            <th style={{ width: '16%' }}>{t('dataSources.history.when', 'Quand')}</th>
                                            <th style={{ width: '22%' }}>{t('dataSources.history.identifiers', 'Identifiants')}</th>
                                            <th style={{ width: '22%' }}>{t('dataSources.verification.current', 'Mécanisme actuel')}</th>
                                            <th style={{ width: '22%' }}>{t('dataSources.verification.new', 'Cette source')}</th>
                                            <th style={{ width: '18%' }}>{t('dataSources.verification.agreement', 'Accord')}</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {shadow.runs.map((r) => {
                                            const differ = Object.entries(r.agreement || {}).filter(([k, v]) => v === false && k !== 'agree').map(([k]) => t(`dataSources.verification.field.${k}`, k)).join(', ');
                                            return (
                                                <tr key={r._id}>
                                                    <td className="u-num">{formatStamp(r.created_at)}</td>
                                                    <td className="ds-mono" title={identifiersText(r.identifiers)}>{identifiersText(r.identifiers) || '—'}</td>
                                                    <td>{r.legacy_found ? `${r.legacy_summary?.carrier || ''} ${r.legacy_summary?.status || ''}`.trim() || '—' : '—'}</td>
                                                    <td>{r.spec_found ? `${r.spec_summary?.carrier || ''} ${r.spec_summary?.status || ''}`.trim() || '—' : (r.reason || '—')}</td>
                                                    <td>
                                                        {r.agreement?.agree
                                                            ? <Badge tone="success" dot>{t('dataToolsUi.source.same', 'Identique')}</Badge>
                                                            : <span title={differ}><Badge tone="warning" dot>{differ || t('dataToolsUi.source.differs', 'Différent')}</Badge></span>}
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            </div>
                        </>
                    )}
                </Card>
            </div>
        );
    };

    const tabs = [
        { key: 'steps', label: t('dataSources.tabs.steps', 'Réglages'), icon: ListChecks },
        { key: 'history', label: t('dataSources.tabs.history', 'Historique'), icon: History },
        doc.shadow_of && { key: 'verification', label: t('dataSources.tabs.verification', 'Vérification'), icon: Scale },
        { key: 'expert', label: t('dataSources.tabs.expert', 'Mode expert'), icon: Code },
    ].filter(Boolean);

    return (
        <>
            <div className="ds-source-head">
                <div className="ds-source-head__id">
                    <Database aria-hidden="true" />
                    <span className="u-code">{doc.key}</span>
                    <Badge tone={STATE_TONE[state]} dot>{t(`dataSources.srcState.${state}`, STATE_TEXT[state] || t('connectorStudio.legacy', 'hérité'))}</Badge>
                    <span className="u-muted">{systemLabel(doc, t)}</span>
                </div>
                <div className="ds-source-head__actions">
                    {canEdit && doc.shadow_of && tab !== 'verification' && (
                        <button type="button" className="u-btn u-btn--primary u-btn--sm" onClick={() => setConfirm('golive')}>{t('dataSources.goLive', 'Remplacer maintenant')}</button>
                    )}
                    {canEdit && (
                        <OverflowMenu label={t('dataToolsUi.more', 'Plus d’actions')} items={[
                            { label: t('dataSources.tabs.expert', 'Mode expert'), icon: Code, onClick: () => onTab('expert') },
                            { separator: true },
                            { label: t('dataToolsUi.source.delete', 'Supprimer la source'), icon: Trash2, danger: true, onClick: () => setConfirm('delete') },
                        ]} />
                    )}
                </div>
            </div>
            {error && <Banner tone="danger">{error}</Banner>}
            {health?.status === 'failing' && (
                <Banner tone="danger" title={reasonText(health.last_reason, t)}>
                    {t('dataToolsUi.source.failingBanner', 'Échec à chaque lecture depuis le {{date}} ({{count}} de suite). Corrigez l’accès, puis vérifiez avec une vraie commande.', {
                        date: formatStamp(health.first_failure_at), count: health.consecutive || 0,
                    })}
                </Banner>
            )}
            {renderKpis()}
            <Tabs label={doc.title || doc.key} active={tab} onChange={(next) => onTab(next)} tabs={tabs} />
            <div role="tabpanel">
                {tab === 'steps' && renderSettings()}
                {tab === 'history' && renderHistory()}
                {tab === 'verification' && renderVerification()}
                {tab === 'expert' && (
                    <ConnectorStudio
                        embedded
                        initialKey={keyName}
                        canEdit={canEdit}
                        onChanged={() => { load(); onChanged?.(); }}
                        onConverted={(key, kind) => { if (key === keyName) load(); onConverted?.(key, kind); }}
                    />
                )}
            </div>

            {confirm === 'delete' && (
                <ConfirmDialog
                    title={t('dataToolsUi.source.deleteTitle', 'Supprimer la source « {{title}} » ?', { title: doc.title || doc.key })}
                    message={t('dataToolsUi.source.deleteMessage', 'Les tickets ne seront plus enrichis par cette source et l’agent ne pourra plus l’interroger. L’historique des lectures est conservé.')}
                    confirmLabel={t('dataToolsUi.source.deleteConfirm', 'Supprimer la source')}
                    cancelLabel={t('common.cancel', 'Annuler')}
                    busy={busy}
                    onConfirm={remove}
                    onCancel={() => setConfirm(null)}
                />
            )}
            {confirm === 'golive' && (
                <ConfirmDialog
                    icon={Scale}
                    title={t('dataToolsUi.source.goLiveTitle', 'Remplacer « {{legacy}} » par « {{title}} » ?', { legacy: doc.shadow_of, title: doc.title || doc.key })}
                    message={t('dataToolsUi.source.goLiveMessage', 'La comparaison s’arrête : dès maintenant, cette source enrichit les tickets à la place du mécanisme actuel.')}
                    confirmLabel={t('dataSources.goLive', 'Remplacer maintenant')}
                    cancelLabel={t('common.cancel', 'Annuler')}
                    busy={busy}
                    onConfirm={goLive}
                    onCancel={() => setConfirm(null)}
                />
            )}
        </>
    );
};

export default SourcePage;
